import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {useToast} from "@/src/modules/core/hooks";
import {capturePushPermissionDenied} from "@/src/modules/core/lib/glitchtip/device-capture";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {useStoredMe} from "@/src/modules/core/store/me";
import {
  isPushRegistrationSupported,
  registerOrSyncPushSubscriptionSafe,
} from "../lib/push-subscription";
import {
  NotificationPermissionStatus,
  useNotificationPermissionStatus,
  useNotificationPermissionStore,
} from "../store/permission";

const isNotificationSupported = isPushRegistrationSupported();

const getBrowserPermission = (): NotificationPermissionStatus => {
  if (!isNotificationSupported) {
    return "unsupported";
  }

  return Notification.permission;
};

export default function useNotificationPermission() {
  const {t} = useTranslation("notifications");
  const status = useNotificationPermissionStatus();
  const setStatus = useNotificationPermissionStore((state) => state.setStatus);
  const toast = useToast();
  const me = useStoredMe();
  const [isRequesting, setIsRequesting] = useState(false);
  const isRequestingRef = useRef(false);

  const syncStatus = useCallback(() => {
    const current = getBrowserPermission();
    setStatus(current);
    return current;
  }, [setStatus]);

  useEffect(() => {
    syncStatus();
  }, [syncStatus]);

  const requestPermission = useCallback(async () => {
    if (isRequestingRef.current) {
      return getBrowserPermission();
    }

    if (!isNotificationSupported) {
      setStatus("unsupported");
      return "unsupported";
    }

    if (isViewingAsUser(me)) {
      return getBrowserPermission();
    }

    isRequestingRef.current = true;
    setIsRequesting(true);

    try {
      const result = await Notification.requestPermission();
      setStatus(result);

      if (result === "denied") {
        capturePushPermissionDenied();
        return result;
      }

      if (result === "default") {
        toast.info(t("toast.permission_not_granted"));
        return result;
      }

      if (result !== "granted") {
        return result;
      }

      await registerOrSyncPushSubscriptionSafe({
        onForbiddenMessage: (message) => {
          toast.error(message);
        },
      });

      return result;
    } finally {
      isRequestingRef.current = false;
      setIsRequesting(false);
    }
  }, [me, setStatus, t, toast]);

  const ensurePermissionRequested = useCallback(async () => {
    return syncStatus();
  }, [syncStatus]);

  return {
    status,
    isSupported: isNotificationSupported,
    isRequesting,
    requestPermission,
    ensurePermissionRequested,
    refreshStatus: syncStatus,
  };
}
